import clsx from 'clsx'
import { usePathname } from 'expo-router'
import { useMemo, type ReactNode, type Ref } from 'react'
import { View, type ViewProps } from 'react-native'

import { Button, type TDefaultButtonProps } from '@/src/elements/button/default'
import type { TImageProps } from '@/src/elements/image'
import { useTabNavigationData } from '@/src/elements/navigation/tab/hooks/useTabNavigationData'
import { TabBar } from '@/src/elements/navigation/tab/TabBar'
import { Typography } from '@/src/elements/text/Typography'
import { CommunityThumbnail } from '@/src/features/community/ui/components/CommunityThumbnail'
import type { TMediumUserApi } from '@/src/features/user/api/userApi.types'
import { TEST_ID } from '@/src/general/constants/testId'
import { useDimensions } from '@/src/general/hooks/useDimensions'

import { UserProfilePreview } from '../user-profile/UserProfilePreview/UserProfilePreview'
import { UserProfileWithModal } from '../user-profile/UserProfilePreviewWithModal/UserProfileWithModal'

const HeaderCommunityElement = ({ variant = 'default', ...otherProps }: TCommunityHeaderProps): ReactNode => (
  <CommunityThumbnail
    titleProps={variant !== 'onGroup' ? { bold: true, size: 'md' } : undefined}
    showImage={variant === 'onGroup'}
    centered
    wrapperClassName="flex-1"
    {...otherProps}
  />
)

type TCommunityHeaderProps =
  | {
      title?: string
      variant?: 'default'
      testId?: string
      imgSource?: undefined
      isLoading?: boolean
    }
  | {
      title: string
      variant: 'onGroup'
      testId?: string
      imgSource: TImageProps['source']
      isLoading?: false
    }
  | {
      title?: undefined
      variant: 'onGroup'
      testId?: string
      imgSource?: undefined
      isLoading: true
    }

type THeaderButtons = (
  | ({
      type: 'icon'
      ref?: Ref<View>
    } & TDefaultButtonProps)
  | {
      type: 'button'
      component: (props: TDefaultButtonProps) => ReactNode
    }
)[]

export type THeaderProps = ViewProps & {
  communityInfos?: TCommunityHeaderProps
  leftButtons?: THeaderButtons
  rightButtons?: THeaderButtons
  showTabs?: boolean
  title?: string
  userInfos?: TMediumUserApi
}

export const Header = ({
  userInfos,
  title,
  communityInfos,
  leftButtons,
  rightButtons,
  className,
  showTabs,
  ...otherProps
}: THeaderProps): ReactNode => {
  const pathname = usePathname()
  const activePage = pathname.split('/')[1]
  const { lessThan } = useDimensions()
  const isLargeScreen = !lessThan.md
  const showTabsNavigation = useMemo(() => {
    return showTabs && isLargeScreen
  }, [showTabs, isLargeScreen])

  const navigationData = useTabNavigationData()

  if ((title && communityInfos) || (title && userInfos))
    console.error('You cannot have both title and communityInfos or userInfos in the Header component')

  if (communityInfos && userInfos)
    console.error('You cannot have both userInfos and communityInfos in the Header component')

  return (
    <View
      role="banner"
      testID={TEST_ID.HEADER.DEFAULT}
      className={clsx(
        'max-w-screen flex w-full flex-row items-center justify-between p-4 native:pt-safe-insets-t',
        className,
      )}
      {...otherProps}
    >
      <View className="flex h-full w-full shrink flex-row items-center gap-2">
        {leftButtons?.map((button, index) =>
          button.type === 'button' ? (
            // oxlint-disable-next-line react/no-array-index-key
            <button.component key={`button-left-${index}`} />
          ) : (
            <Button key={button.startIcon} colors="default" isIconOnly contrast="low" variant="light" {...button} />
          ),
        )}
        {userInfos && !communityInfos && (
          <UserProfileWithModal user={userInfos}>
            <UserProfilePreview.Avatar user={userInfos} />
          </UserProfileWithModal>
        )}
        {communityInfos && <HeaderCommunityElement {...communityInfos} />}

        {!!title && (
          <Typography bold center className="w-full">
            {title}
          </Typography>
        )}
      </View>

      {!!showTabsNavigation && (
        <View role="navigation" className="flex w-fit shrink-0 flex-row justify-center" testID={TEST_ID.NAVIGATION.TAB}>
          <TabBar activePage={activePage} navigationData={navigationData} className="z-30 w-[375px] shadow-none" />
        </View>
      )}
      <View
        className={clsx({
          'flex w-full shrink flex-row justify-end gap-2': showTabsNavigation,
          'flex flex-row gap-2': !showTabsNavigation,
        })}
      >
        {rightButtons?.map((button, index) =>
          button.type === 'button' ? (
            // oxlint-disable-next-line react/no-array-index-key
            <button.component testID={`${button.type}-right-${index}`} key={`${button.type}-right-${index}`} />
          ) : (
            <Button
              testID={`${button.type}-right-${index}`}
              key={button.startIcon}
              colors="default"
              isIconOnly
              contrast="low"
              variant="light"
              {...button}
            />
          ),
        )}
      </View>
    </View>
  )
}
